import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  formatPhoneNumber,
  formatPhoneNumberIntl,
  parsePhoneNumber,
} from "react-phone-number-input";
import { type Contact } from "../types";
import { type MatterInsurance, type MatterVehicle } from "./../types/Matter";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
// ------------ DATE ------------
export function formatDate(
  date: string | Date | undefined | null,
  includeYear = true,
) {
  if (!date || !dayjs(date).isValid()) return;

  return dayjs
    .utc(date)
    .format(includeYear ? "MMM D, YYYY" : "MMM D")
    .toString();
}

export function formatNumericDate(date: string | Date | undefined | null) {
  if (!date || !dayjs(date).isValid()) return;

  return dayjs.utc(date).format("MM/DD/YYYY").toString();
}

export function formatDateTime(
  date: string | Date | undefined,
  showTimezone = true,
) {
  if (!date || !dayjs(date).isValid()) return;

  return dayjs(date)
    .format(`MMM D, YYYY h:mm A${showTimezone ? " (z)" : ""}`)
    .toString();
}

export function formatTime(time: string | Date | undefined) {
  if (!time || !dayjs(time, "HH:mm").isValid()) return;

  return dayjs(time, "HH:mm").format("h:mm A").toString();
}

// ------------ FIRM ------------
export const formatUsersName = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null,
) => {
  if (!user) return;
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return name || undefined;
};

export const trimText = (value: string | null | undefined) =>
  value?.trim() ?? "";

// ------------ CONTACTS ------------
export const formatContactsName = (
  contact?: Partial<
    Pick<
      Contact,
      | "individualFirstName"
      | "organizationName"
      | "type"
      | "individualLastName"
      | "individualNickname"
      | "groupName"
    >
  >,
): string | undefined => {
  if (!contact) return undefined;
  if (contact.type === "organization") {
    const v = contact.organizationName;
    if (v == null || v === "") return undefined;
    return String(v);
  }
  if (contact.type === "group") {
    const v = contact.groupName;
    if (v == null || v === "") return undefined;
    return String(v);
  }
  const formattedNickname = contact.individualNickname
    ? `"${contact.individualNickname}"`
    : undefined;
  const nameParts = [
    contact.individualFirstName?.trim(),
    formattedNickname,
    contact.individualLastName?.trim(),
  ].filter(Boolean);
  if (nameParts.length === 0) {
    return undefined;
  }
  return nameParts.join(" ");
};

// ------------ Phone Numbers ------------
type PhoneNumberDisplayValue = {
  number?: string | null;
  extension?: string | null;
};

/** Pass a phone object so extensions can be displayed. */
type LegacyPhoneNumberDisplayValue = string;

export const formatLocalOrIntlPhoneNumber = (
  phone?: PhoneNumberDisplayValue | LegacyPhoneNumberDisplayValue,
) => {
  const phoneValue = typeof phone === "string" ? { number: phone } : phone;
  const number = phoneValue?.number;
  if (!number) return "";
  const country = parsePhoneNumber(number);
  const trimmedExtension = phoneValue.extension?.trim();

  // If we can't find a country we will just return the number
  if (!country)
    return `${number}${trimmedExtension ? ` x${trimmedExtension}` : ""}`;

  const formattedNumber =
    country.countryCallingCode === "1"
      ? formatPhoneNumber(number)
      : formatPhoneNumberIntl(number);

  return `${formattedNumber}${trimmedExtension ? ` x${trimmedExtension}` : ""}`;
};

// ------------ Address ------------
export const formatAddress = ({
  street1,
  street2,
  city,
  state,
  zipCode,
  country,
}: {
  street1?: string | null;
  street2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  country?: string | null;
}) => {
  const street = [street1, street2]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" ");
  const locality = [city, state]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(", ");
  const cityStateZip = [locality, zipCode?.trim()].filter(Boolean).join(" ");

  return [street, cityStateZip, country?.trim()].filter(Boolean).join(", ");
};

export const formatAddressLine1 = ({
  street1,
  street2,
}: {
  street1?: string;
  street2?: string;
}) => {
  return [street1, street2].filter(Boolean).join(", ");
};

export const formatAddressLine2 = ({
  city,
  state,
  zipCode,
  country,
  showCountry = false,
}: {
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  showCountry?: boolean;
}) => {
  let stringBuilder = "";
  if (city) stringBuilder += `${city}, `;
  if (state) stringBuilder += state;
  if (zipCode) stringBuilder += ` ${zipCode}`;
  if (showCountry && country) stringBuilder += `, ${country}`;
  return stringBuilder;
};

// ------------ VEHICLE ------------
export const formatVehicleTitle = (
  vehicle: Pick<MatterVehicle, "make" | "model" | "year">,
) => {
  const stringBuilder = [];
  const year = vehicle.year;
  const make = vehicle.make;
  const model = vehicle.model;
  if (year) stringBuilder.push(year);
  if (make) stringBuilder.push(make);
  if (model) stringBuilder.push(model);
  return stringBuilder.join(" ").trim();
};

export const formatInsuranceTitle = ({
  insurance,
}: {
  insurance?: Pick<MatterInsurance, "connections" | "id" | "type">;
}) => {
  if (!insurance) return;
  const getConnection = (role: string) =>
    insurance.connections.find(
      (connection) =>
        connection.state === "active" &&
        (connection.connectionType === `insurance_claim_to_${role}` ||
          connection.connectionType === `insurance_claim_to_legacy_${role}`),
    );
  const insured = getConnection("insured");
  const policyHolder = getConnection("policy_holder");
  const insurer = getConnection("insurer");
  const bodilyInjuryAdjuster = getConnection("bodily_injury_adjuster");
  const propertyDamageAdjuster = getConnection("property_damage_adjuster");
  const medPayAdjuster = getConnection("medpay_adjuster");
  const adjusterSupervisor = getConnection("adjuster_supervisor");
  const stringBuilder = [];
  const insurerName = insurer
    ? formatContactsName(insurer.contact ?? undefined) || "<No Insurer>"
    : "<No Insurer>";
  const insuredName = insured
    ? formatContactsName(insured.contact ?? undefined) || "<No Insured>"
    : "<No Insured>";
  const policyHolderName = policyHolder
    ? formatContactsName(policyHolder.contact ?? undefined) ||
      "<No Policy Holder>"
    : "<No Policy Holder>";
  const insuranceType = insurance.type;
  stringBuilder.push(insurerName);
  stringBuilder.push(insuredName);
  stringBuilder.push(insuranceType);

  return {
    title: stringBuilder.join(" - "),
    insurerPart: insurerName,
    insurer: insurer,
    insuredPart: insuredName,
    insured: insured,
    bodilyInjuryAdjuster,
    propertyDamageAdjuster,
    medPayAdjuster,
    adjusterSupervisor,
    insuranceTypePart: insuranceType,
    policyHolderPart: policyHolderName,
    policyHolder: policyHolder,
    hasPolicyHolder: !!policyHolder,
  };
};
